import React, { useContext, useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { HiHome } from "react-icons/hi";
import { Breadcrumb } from "flowbite-react";
import { cartContext } from "../../Context/CartContext";
import FilterSection from "../FilterSection/FilterSection";
import ProductCard from "./ProductCard";
import LoadingSpinner from "./LoadingSpinner";
import PromotionalOffers from "../ProductDetails/PromotionalOffers";
import OfferModal from "./offerModal";

export default function ProductList() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // State variables
  const [isModalOpen, setIsModalOpen] = useState(false);
  const urlCategoryId = queryParams.get("category");
  const urlSubcategoryId = queryParams.get("subcategory");
  const urlPage = parseInt(queryParams.get("page")) || 1;

  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(urlPage);
  const [totalPages, setTotalPages] = useState(0);
  const [loadingProducts, setLoadingProducts] = useState({});
  const [selectedCategoryId, setSelectedCategoryId] = useState(urlCategoryId || "");
  const [categoryOffers, setCategoryOffers] = useState(null);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState(urlSubcategoryId || "");
  const [selectedCategoryName, setSelectedCategoryName] = useState("");
  const [selectedSubcategoryName, setSelectedSubcategoryName] = useState("");
  const [activeFilters, setActiveFilters] = useState({
    searchQuery: "",
    categoryId: urlCategoryId || "",
    subcategoryId: urlSubcategoryId || "",
    sortOrder: "",
  });

  const { setnumOfCartItems } = useContext(cartContext);



  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      const { data } = await axios.get("https://api.elza2.store/api/v1/categories");
      setCategories(data.categories);

      if (urlCategoryId) {
        const selectedCategory = data.categories.find((cat) => cat._id === urlCategoryId);
        if (selectedCategory) {
          setSelectedCategoryName(selectedCategory.name);
          setCategoryOffers(selectedCategory.promotion || null);
        }
      }
    } catch (error) {
      console.error("Failed to fetch categories", error);
    }
  };

  // Update URL with current filters and page
  const updateURL = (filters, page) => {
    const newSearch = new URLSearchParams(location.search);

    if (filters.categoryId) {
      newSearch.set("category", filters.categoryId);
    } else {
      newSearch.delete("category");
    }

    if (filters.subcategoryId) {
      newSearch.set("subcategory", filters.subcategoryId);
    } else {
      newSearch.delete("subcategory");
    }

    if (page > 1) {
      newSearch.set("page", page.toString());
    } else {
      newSearch.delete("page");
    }

    navigate(`${location.pathname}?${newSearch.toString()}`, { replace: true });
  };

  // Build products API URL based on filters
  const buildProductsUrl = (filters, page) => {
    const { searchQuery, categoryId, subcategoryId, sortOrder } = filters;
    let baseUrl = "https://api.elza2.store/api/v1/products";

    if (subcategoryId) {
      baseUrl = `${baseUrl}/subcategory/${subcategoryId}`;
    } else if (categoryId) {
      baseUrl = `${baseUrl}/category/${categoryId}`;
    }

    const params = new URLSearchParams({
      page: page.toString(),
      limit: "40",
      ...(searchQuery && { search: searchQuery }),
      ...(sortOrder && { sort: sortOrder }),
    });

    return `${baseUrl}?${params.toString()}`;
  };

  const fetchInProgress = useRef(false);

  // Fetch products from API
  const fetchProducts = async (filters = activeFilters, page = 1) => {
    if (fetchInProgress.current) return;
    fetchInProgress.current = true;

    try {
      setLoading(true);
      window.scrollTo(0, 0);

      const url = buildProductsUrl(filters, page);
      const { data } = await axios.get(url);

      setProducts(data.products);
      setCurrentPage(data.page);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error("Failed to fetch products", error);
    } finally {
      fetchInProgress.current = false;
      setLoading(false);
    }
  };

  // Handle adding product to cart
  const handleAddToCart = async (productId, quantity) => {
    if (!localStorage.getItem("Auth")) {
      navigate("/login");
      return;
    }

    setLoadingProducts((prev) => ({ ...prev, [productId]: true }));
    try {
      const response = await axios.post(
        "https://api.elza2.store/api/v1/cart",
        { product: productId, quantity },
        { headers: { Authorization: localStorage.getItem("Auth") } }
      );
      setnumOfCartItems(response.data.numOfCartItems);
    } catch (error) {
      console.error("Failed to add to cart", error);
    } finally {
      setLoadingProducts((prev) => ({ ...prev, [productId]: false }));
    }
  };

  // Handle product click navigation
  const handleProductClick = (productId) => {
    sessionStorage.setItem("scrollPosition", window.scrollY.toString());
    sessionStorage.setItem("productListPage", currentPage.toString());
    navigate(`/product-details/${productId}`);
  };

  // Handle filter changes
  const handleFilterChange = async (filters) => {
    const newCategoryId = filters.categoryId || "";
    const newSubcategoryId = filters.subcategoryId || "";

    setSelectedCategoryId(newCategoryId);
    setSelectedSubcategoryId(newSubcategoryId);
    setActiveFilters(filters);
    setCurrentPage(1);

    if (newCategoryId && categories.length > 0) {
      const selectedCategory = categories.find((cat) => cat._id === newCategoryId);
      setSelectedCategoryName(selectedCategory ? selectedCategory.name : "");
    } else {
      setSelectedCategoryName("");
    }

    await fetchProducts(filters, 1);
    updateURL(filters, 1);
  };

  // Handle page change
  const handlePageChange = async (page) => {
    setCurrentPage(page);
    await fetchProducts(activeFilters, page);
    updateURL(activeFilters, page);
  };

  // Effect for category selection
  useEffect(() => {
    if (selectedCategoryId && categories.length > 0) {
      const selectedCategory = categories.find((cat) => cat._id === selectedCategoryId);
      setSelectedCategoryName(selectedCategory?.name || "");
      setCategoryOffers(selectedCategory?.promotion || null);
    } else {
      setSelectedCategoryName("");
      setCategoryOffers(null);
    }
  }, [selectedCategoryId, categories]);

  // Main initialization effect
  useEffect(() => {
    let isMounted = true;

    const initializeAndFetch = async () => {
      if (categories.length === 0) {
        await fetchCategories();
      }

      const page = parseInt(queryParams.get("page")) || 1;
      const categoryId = queryParams.get("category") || "";
      const subcategoryId = queryParams.get("subcategory") || "";

      const newFilters = {
        ...activeFilters,
        categoryId,
        subcategoryId,
      };

      if (isMounted) {
        setActiveFilters(newFilters);
        setSelectedCategoryId(categoryId);
        setSelectedSubcategoryId(subcategoryId);
        setCurrentPage(page);
        await fetchProducts(newFilters, page);
      }
    };

    initializeAndFetch();

    return () => {
      isMounted = false;
    };
  }, [location.search]);

  // Effect for category offers modal
  useEffect(() => {
    if (categoryOffers) {
      setIsModalOpen(true);
    }
  }, [categoryOffers]);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  if (loading) return <LoadingSpinner />;

  // Render customize button for Photo Cards
  const renderCustomizeButton = () => {
    if (selectedCategoryName === "Photo Cards") {
      return (
        <div className="mb-4 text-center">
          <Link
            to="/custom"
            className="inline-block explore-btn px-6 py-3 rounded-lg transition-colors duration-300 shadow-md"
          >
            Create your own Photo Card
          </Link>
        </div>
      );
    }
    return null;
  };

  // Render pagination controls
  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded-md ${
            currentPage === i
              ? "bg-black text-white"
              : "bg-gray-200 hover:bg-gray-300"
          }`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="flex justify-center gap-2 mt-6">
        <button
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded-md bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
        >
          {`<<`} First
        </button>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded-md bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
        >
          {`<`} Prev
        </button>
        {pages}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded-md bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
        >
          Next {`>`}
        </button>
        <button
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded-md bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
        >
          Last {`>>`}
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen py-6">
      {/* <Snow /> */}
      <div className="max-w-9xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb Navigation */}
        <Breadcrumb className="px-5 py-3 rounded-lg mb-6">
          <Breadcrumb.Item>
            <Link
              to="/"
              className="flex items-center text-gray-700 hover:text-blue-600"
            >
              <HiHome className="w-5 h-5 mr-1" />
              Home
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/products" className="text-gray-700 hover:text-blue-600">
              Products
            </Link>
          </Breadcrumb.Item>
          {selectedCategoryName && (
            <Breadcrumb.Item>
              <span className="text-gray-400">{selectedCategoryName}</span>
            </Breadcrumb.Item>
          )}
          {selectedSubcategoryName && (
            <Breadcrumb.Item>
              <span className="text-gray-400">{selectedSubcategoryName}</span>
            </Breadcrumb.Item>
          )}
        </Breadcrumb>

        {/* Modal Container */}
        <div className="grid grid-cols-12 gap-4">
          {console.log("categoryOffers", categoryOffers)}
          {categoryOffers && categoryOffers.discounts.length > 0 && (
            <OfferModal
              isOpen={isModalOpen}
              onClose={closeModal}
              promotion={categoryOffers}
            />
          )}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-12 gap-4">
          {/* Promotional Offers */}
          <PromotionalOffers productDetails={categoryOffers} />

          {/* Filter Section - Left Sidebar */}
          <div className="col-span-12 lg:col-span-3">
            <FilterSection
              categories={categories}
              selectedCategoryId={selectedCategoryId}
              selectedSubcategoryId={selectedSubcategoryId}
              onFilterChange={handleFilterChange}
            />
            

          </div>

          {/* Products Section - Main Content */}
          <div className="col-span-12 lg:col-span-9">
  

            {/* Custom Item Creation Button */}
            <div className="mb-4 text-center">
              <Link
                to="/custom"
                className="inline-block explore-btn px-6 py-3 rounded-lg transition-colors duration-300 shadow-md"
              >
                Create your own Custom Item ➕ 🎨
              </Link>
            </div>

            {/* Photo Cards Special Section */}
            {selectedCategoryName === "Photo Cards" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <div className="cursor-pointer bg-white border-2 hover:scale-105 hover:transition-all rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 h-full">
                  <Link to={`/custom`} className="block">
                    <img
                      src="https://res.cloudinary.com/dd5v7ajfk/image/upload/v1735441418/ecommerce/categories/arfvyansfz8csuoeklvg.jpg"
                      alt="photo card"
                    />
                  </Link>
                  <div className="p-3">
                    <Link to={`/custom`} className="block mb-2">
                      <h3 className="text-sm font-semibold text-gray-800 hover:text-red-700 transition-colors line-clamp-1">
                        Create your custom Photo Card
                      </h3>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                        Photo Card
                      </p>
                    </Link>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-lg font-extrabold text-main">
                        12 EGP
                      </span>
                    </div>
                    <button
                      onClick={() => navigate("/custom")}
                      disabled={loading}
                      className="flex-1 p-4 explore-btn py-2 rounded-md hover:explore-btn transition-colors duration-200 flex items-center justify-center gap-2 text-sm font-medium"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>Create your Photo Card</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Products Grid or No Products Message */}
            {products.length === 0 && selectedCategoryName !== "Photo Cards" ? (
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <h3 className="text-lg font-semibold text-gray-700">
                  No products found
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Try adjusting your filters or{" "}
                  <Link
                    to="/categories"
                    className="text-blue-600 hover:underline"
                  >
                    browse all categories
                  </Link>
                </p>
              </div>
            ) : (
              <>
                {/* Products Grid with Integrated Ads - Only on larger screens */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-3 sm:gap-4">
                  {products.map((item, mapIndex) => {
                    
                    // Render Product Card
                    return (
                      <div key={item._id} className="col-span-1 w-full">
                        <ProductCard
                          product={item}
                          onAddToCart={handleAddToCart}
                          onClick={() => handleProductClick(item._id)}
                          loading={loadingProducts[item._id]}
                        />
                        {selectedCategoryName === "Photo Cards" &&
                          renderCustomizeButton(item)}
                      </div>
                    );
                  })}
                </div>

                {/* Pagination Controls */}
                {renderPagination()}

                {/* Page Info */}
                <div className="flex justify-center mt-6">
                  showing page {currentPage} of {totalPages}
                </div>

                {/* Progress Bar */}
                <div className="flex justify-center mt-6">
                  <div className="w-full max-w-52 bg-gray-200 rounded-full h-1.5 dark:bg-gray-700">
                    <div
                      className="bg-black h-1.5 rounded-full"
                      style={{
                        width: `${(currentPage / totalPages) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}